import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useEffect } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { useUpdateSection } from '../hooks/use-section-mutations'
import type { MenuContents } from '@/api/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCategories } from '@/features/categories/hooks/use-categories'

const buildEditSectionSchema = (tMenus: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .min(1, tMenus('validation.sectionNameRequired'))
      .max(200, tMenus('validation.sectionNameMax')),
    categoryId: z.number().int().optional(),
    sortOrder: z.number().int(),
    dineInOnly: z.boolean(),
    dailySpecial: z.boolean(),
    menuCard: z.boolean(),
  })

type EditSectionSchema = ReturnType<typeof buildEditSectionSchema>
type EditSectionFormData = z.infer<EditSectionSchema>

interface EditSectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  menuId: number
  section: MenuContents['sections'][0]
}

export function EditSectionDialog({
  open,
  onOpenChange,
  menuId,
  section,
}: EditSectionDialogProps) {
  const { t: tMenus } = useTranslation('menus')
  const updateSection = useUpdateSection()
  const { data: categoriesData } = useCategories()

  const form = useForm<EditSectionFormData>({
    resolver: zodResolver(buildEditSectionSchema(tMenus)),
    defaultValues: {
      name: section.name,
      categoryId: section.categoryId,
      sortOrder: section.sortOrder,
      dineInOnly: section.dineInOnly ?? false,
      dailySpecial: section.dailySpecial ?? false,
      // Fail open, same as the client: a section saved before this flag existed reads as visible.
      menuCard: section.menuCard ?? true,
    },
  })

  // Reset form when section changes
  useEffect(() => {
    form.reset({
      name: section.name,
      categoryId: section.categoryId,
      sortOrder: section.sortOrder,
      dineInOnly: section.dineInOnly ?? false,
      dailySpecial: section.dailySpecial ?? false,
      menuCard: section.menuCard ?? true,
    })
  }, [section, form])

  const onSubmit = (data: EditSectionFormData) => {
    updateSection.mutate(
      { menuId, sectionId: section.id, data },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      },
    )
  }

  const categories = categoriesData?.items || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tMenus('section.editTitle')}</DialogTitle>
          <DialogDescription>
            {tMenus('section.editDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tMenus('section.name')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={tMenus('section.namePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tMenus('section.categoryOptional')}</FormLabel>
                  <Select
                    value={field.value?.toString() || 'none'}
                    onValueChange={(value) =>
                      field.onChange(
                        value === 'none' ? undefined : parseInt(value),
                      )
                    }
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={tMenus('section.noCategory')}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">
                        {tMenus('section.noCategory')}
                      </SelectItem>
                      {categories.map((category) => (
                        <SelectItem
                          key={category.id}
                          value={category.id.toString()}
                        >
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {tMenus('section.categoryHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tMenus('section.sortOrder')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    {tMenus('section.sortHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="menuCard"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{tMenus('section.menuCard')}</FormLabel>
                    <FormDescription>
                      {tMenus('section.menuCardHint')}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dineInOnly"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{tMenus('section.dineInOnly')}</FormLabel>
                    <FormDescription>
                      {tMenus('section.dineInOnlyHint')}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dailySpecial"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{tMenus('section.dailySpecial')}</FormLabel>
                    <FormDescription>
                      {tMenus('section.dailySpecialHint')}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateSection.isPending}
              >
                {tMenus('section.cancel')}
              </Button>
              <Button type="submit" disabled={updateSection.isPending}>
                {updateSection.isPending
                  ? tMenus('section.saving')
                  : tMenus('section.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
